import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';

import ClickableIcon from '~components/ClickableIcon';
import { faFilePdf, faSpinner } from '~components/icon/iconLibrary';

import { useLazyGetBudgetQuery } from '~support-at-home/actions/budgets';
import { useLazyGetClientHcpUnspentFundsQuery } from '~support-at-home/actions/clientHcpUnspentFunds';

import { User } from '~main/reducers/login';

import { GenerateBudgetPDF } from './GenerateBudgetPDF';

interface ComponentProps {
  id: string;
  name: string;
  user?: User;
}

const ExportBudgetToPDF: React.FC<ComponentProps> = ({ id, name, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [getBudgetTrigger, { isFetching: isBudgetFetching, error: budgetError }] = useLazyGetBudgetQuery();
  const [getHcpTrigger, { isFetching: isHCPFetching, error: hcpError }] = useLazyGetClientHcpUnspentFundsQuery();

  const error = budgetError || hcpError;
  const loading = isLoading || isBudgetFetching || isHCPFetching;

  const generatePDF = async () => {
    setIsLoading(true);
    try {
      const budget = await getBudgetTrigger({ id }).unwrap();
      const hcpData = await getHcpTrigger({ client_id: budget.client_id }).unwrap();

      if (!budget) {
        console.warn('Budget data not yet available for export.');
        return;
      }

      const pdfDocument = <GenerateBudgetPDF budget={budget} user={user} hcpData={hcpData} />;

      // Convert to blob and download
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);

      // Download file
      const link = document.createElement('a');
      link.href = url;
      link.download = `budget-${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    console.error('Failed to load budget for PDF export:', error);
  }

  return (
    <div>
      {!loading ? (
        <ClickableIcon id="export-pdf-icon" toolTip="Export To PDF" icon={faFilePdf} onClick={generatePDF} />
      ) : (
        <ClickableIcon id="export-pdf-icon-loading" toolTip="Generating PDF..." icon={faSpinner} iconClassname="spin" />
      )}
    </div>
  );
};

export default ExportBudgetToPDF;
